import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';

const MESSAGES: Record<string, string> = {
  wrong_password: 'Núverandi lykilorð er rangt.',
  too_short: 'Nýja lykilorðið þarf að vera minnst 10 stafir.',
  same_password: 'Nýja lykilorðið er það sama og það gamla.',
  unauthorized: 'Innskráningin er útrunnin. Skráðu þig inn aftur.',
};

export default function Lykilord({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const mismatch = again.length > 0 && next !== again;
  const tooShort = next.length > 0 && next.length < 10;
  const ready = current && next.length >= 10 && next === again && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError('');
    try {
      await api.changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setAgain('');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'network';
      setError(MESSAGES[code] ?? 'Ekki tókst að breyta lykilorðinu.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog className="dlg" ref={ref} onClose={onClose} onCancel={onClose}>
      <form className="form" onSubmit={submit}>
        <div className="dlg-h">
          <h2>Breyta lykilorði</h2>
          <button type="button" className="ico" aria-label="Loka" onClick={onClose}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
              <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
            </svg>
          </button>
        </div>

        {done ? (
          <>
            <p className="ok-note">
              Lykilorðinu hefur verið breytt. Þú ert enn innskráð/ur hér, en aðrar innskráningar
              hafa verið gerðar óvirkar.
            </p>
            <button type="button" className="btn wide" onClick={onClose}>
              Loka
            </button>
          </>
        ) : (
          <>
            {error && <div className="alert" role="alert">{error}</div>}

            <div className="fld">
              <label className="lbl" htmlFor="pw-cur">Núverandi lykilorð</label>
              <input
                id="pw-cur"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="pw-new">Nýtt lykilorð</label>
              <input
                id="pw-new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                aria-describedby="pw-hint"
                required
              />
              <span id="pw-hint" className={`hint${tooShort ? ' bad' : ''}`}>
                {tooShort ? `Vantar ${10 - next.length} stafi — minnst 10` : 'Minnst 10 stafir'}
              </span>
            </div>

            <div className="fld">
              <label className="lbl" htmlFor="pw-again">Endurtaka nýtt lykilorð</label>
              <input
                id="pw-again"
                type="password"
                autoComplete="new-password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                required
              />
              {mismatch && <span className="hint bad">Lykilorðin stemma ekki.</span>}
            </div>

            <div className="row" style={{ justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn ghost" onClick={onClose}>
                Hætta við
              </button>
              <button className="btn" type="submit" disabled={!ready}>
                {busy ? 'Vista…' : 'Breyta'}
              </button>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}
